import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { permissionLevelEnum } from '../../../../constants';
import { useFormAccessByActiveUsersQuery } from '../../../../hooks/queries/useFormAccessQuery';
import { useLatestFormTemplatesQuery } from '../../../../hooks/queries/useFormTemplatesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import FormAccessCell from '../../../cells/FormAccessCell';
import GenericAgGridTable from '../../GenericAgGridTable';

const FormsUsersAccessTable: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { data: forms, isLoading, refetch } = useLatestFormTemplatesQuery({});
    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const { data: dataFormAccesses, refetch: refetchFormAccesses } = useFormAccessByActiveUsersQuery({});

    const columnDefs: ColDef<User>[] = useMemo(
        () => [
            {
                headerName: capitalizeFirstLetter(t('user')),
                valueGetter: (params) => formatName(params.data?.firstName, params.data?.lastName),
                initialFlex: 1,
                minWidth: 300,
                colId: 'user',
            },
            {
                headerName: capitalizeFirstLetter(t('privateFormsAccess')),
                field: '_id',
                colId: 'privateFormsAccess',
                initialWidth: 400,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <FormAccessCell
                            userId={params.value}
                            formAccesses={dataFormAccesses || []}
                            forms={forms || []}
                            isPublic={false}
                            userName={formatName(params.data?.firstName, params.data?.lastName)}
                            refetch={refetchFormAccesses}
                        />
                    );
                },
            },
            {
                headerName: capitalizeFirstLetter(t('publicFormsAccess')),
                field: '_id',
                colId: 'publicFormsAccess',
                initialWidth: 400,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <FormAccessCell
                            userId={params.value}
                            formAccesses={dataFormAccesses || []}
                            forms={forms || []}
                            isPublic={true}
                            userName={formatName(params.data?.firstName, params.data?.lastName)}
                            refetch={refetchFormAccesses}
                        />
                    );
                },
            },

            ...((user?.profileId.permsLevel === permissionLevelEnum.ADMIN
                ? [
                      {
                          headerName: capitalizeFirstLetter(t('_id')),
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : []) as ColDef<User>[]),
        ],
        [forms, dataFormAccesses, t, user]
    );

    return (
        <>
            <GenericAgGridTable
                columnDefs={columnDefs}
                rowData={dataUsers?.sort((a, b) => formatName(a.firstName, a.lastName).localeCompare(formatName(b.firstName, b.lastName))) || []}
                loading={isUsersLoading}
                exportCsvFileName="forms-users-access"
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
        </>
    );
};

export default FormsUsersAccessTable;
